import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TabHistoryEntry, TabHistoryGroup } from '../types/tabHistoryTypes';
import type { Tab } from '../../workspace/core/types/workspaceTypes';
import { useLicenseStore } from '../../../core/licensing';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';
import { mapIfArray, recordList, rewritePersistedState } from '../../../core/storage/rewritePersistedState';
import { showPersistWriteFailureToast } from '../../../components/ui/persistWriteFailureToast';
import { exceedsPersistedSqlLimit } from '../../../types/editorTextLimits';
import { isUntouchedPendingReload } from '../../sessions/services/sessionPersistenceCap';
import { safeTab, safeTabConnectionInfo, safeTabTitle } from '../../workspace/core/store/safeTabText';

/** Maximum closed tabs for Personal mode */
const MAX_CLOSED_TABS_PERSONAL = 5;

/** No limit for Pro mode (but we'll cap at a reasonable number for performance) */
const MAX_CLOSED_TABS_PRO = 100;

/**
 * Persist-time cap: an entry whose script exceeds `PERSISTED_SQL_LIMIT_CHARS`
 * is written without it (`queryOmitted: true`, file binding kept for the
 * reload-on-reopen path in tabHistoryReopen.ts). Same references when nothing
 * needs capping.
 */
export function capClosedTabsForPersistence(
  closedTabsBySession: Record<string, TabHistoryEntry[]>,
): Record<string, TabHistoryEntry[]> {
  let changed = false;
  const result: Record<string, TabHistoryEntry[]> = {};
  for (const [sessionId, entries] of Object.entries(closedTabsBySession)) {
    let sessionChanged = false;
    const capped = entries.map((entry) => {
      if (!exceedsPersistedSqlLimit(entry.query)) return entry;
      sessionChanged = true;
      const { query: _dropped, fileLastSavedHash: _hash, ...rest } = entry;
      void _dropped;
      void _hash;
      return { ...rest, queryOmitted: true };
    });
    if (sessionChanged) changed = true;
    result[sessionId] = sessionChanged ? capped : entries;
  }
  return changed ? result : closedTabsBySession;
}

type TabHistoryState = {
  /** Current session ID for scoping history */
  currentSessionId: string | null;

  /** Closed tabs per session, keyed by session ID */
  closedTabsBySession: Record<string, TabHistoryEntry[]>;

  /** Dialog open state */
  isDialogOpen: boolean;

  /**
   * Set the current session ID (called when session changes)
   */
  setCurrentSession: (sessionId: string | null) => void;

  /**
   * Add a closed tab to history for the current session
   */
  addClosedTab: (tab: Tab) => void;

  /**
   * Get closed tabs for current session
   */
  getClosedTabs: () => TabHistoryEntry[];

  /**
   * Get closed tabs grouped by connection for current session
   */
  getGroupedClosedTabs: () => TabHistoryGroup[];

  /**
   * Remove a closed tab from history (after re-opening)
   */
  removeClosedTab: (id: string) => void;

  /**
   * Clear all closed tabs for current session
   */
  clearClosedTabs: () => void;

  /**
   * Clear closed tabs for a specific session
   */
  clearSessionHistory: (sessionId: string) => void;

  /**
   * Open/close the dialog
   */
  openDialog: () => void;
  closeDialog: () => void;
};

/** A stored `closedAt` as a Date: a string, number or Date as it is; anything else (a damaged file) the epoch. */
function storedDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date(0);
}

/** The stored history document with only its entries' secret-bearing fields made safe (same object when none changed). */
function safeStoredHistory(stored: Record<string, unknown>): Record<string, unknown> {
  const bySession = stored.closedTabsBySession;
  if (bySession === undefined || bySession === null) return stored;
  // Not a session map (a damaged file — an array of entries, a string): it cannot be read, so it is not kept (round 4).
  if (typeof bySession !== 'object' || Array.isArray(bySession)) return { ...stored, closedTabsBySession: {} };
  let changed = false;
  const next: Record<string, unknown> = {};
  for (const [sessionId, entries] of Object.entries(bySession as Record<string, unknown>)) {
    next[sessionId] = mapIfArray(recordList(entries), safeTab);
    if (next[sessionId] !== entries) changed = true;
  }
  return changed ? { ...stored, closedTabsBySession: next } : stored;
}

export const useTabHistoryStore = create<TabHistoryState>()(
  persist(
    (set, get) => ({
      currentSessionId: null,
      closedTabsBySession: {},
      isDialogOpen: false,

      setCurrentSession: (sessionId) => {
        set({ currentSessionId: sessionId });
      },

      addClosedTab: (tab) => {
        const { currentSessionId } = get();
        if (!currentSessionId) return; // No session, don't track

        const isPro = useLicenseStore.getState().isPro();
        const maxTabs = isPro ? MAX_CLOSED_TABS_PRO : MAX_CLOSED_TABS_PERSONAL;

        // Extract relevant data based on tab type
        let query: string | undefined;
        let queryOmitted: boolean | undefined;
        let tableName: string | undefined;
        let schema: string | undefined;
        let database: string | undefined;
        let planView: 'single' | 'compare' | undefined;
        let filePath: string | undefined;
        let fileLastSavedHash: string | undefined;
        let baselineFilePath: string | undefined;
        let compareFilePath: string | undefined;

        if (tab.type === 'query' && tab.content && isUntouchedPendingReload(tab.content)) {
          // Closed before its omitted script was reloaded, placeholder
          // untouched: record the file binding, not the placeholder, so
          // reopen reloads from disk.
          queryOmitted = true;
          filePath = tab.content.pendingFileReload;
        } else if (tab.type === 'query') {
          query = tab.content?.query;
          filePath = tab.content?.filePath;
          fileLastSavedHash = tab.content?.fileLastSavedHash;
        } else if (tab.type === 'table-explorer') {
          tableName = tab.content?.tableName;
          schema = tab.content?.schema;
          database = tab.content?.database;
        } else if (tab.type === 'plan') {
          planView = tab.content?.view ?? 'single';
          filePath = tab.content?.filePath;
          baselineFilePath = tab.content?.baselineFilePath;
          compareFilePath = tab.content?.compareFilePath;
        }

        const entry: TabHistoryEntry = {
          id: tab.id,
          // Safe text only (security slice): the history is persisted and shown in Recent tabs / the history dialog.
          title: safeTabTitle(tab.title),
          customTitle: tab.customTitle,
          type: tab.type,
          connectionInfo: safeTabConnectionInfo(tab.connectionInfo),
          closedAt: new Date(),
          query,
          ...(queryOmitted ? { queryOmitted } : {}),
          tableName,
          schema,
          database,
          planView,
          filePath,
          fileLastSavedHash,
          baselineFilePath,
          compareFilePath,
        };

        set((state) => {
          const sessionTabs = state.closedTabsBySession[currentSessionId] || [];
          return {
            closedTabsBySession: {
              ...state.closedTabsBySession,
              [currentSessionId]: [entry, ...sessionTabs].slice(0, maxTabs),
            },
          };
        });
      },

      getClosedTabs: () => {
        const { currentSessionId, closedTabsBySession } = get();
        if (!currentSessionId) return [];
        return closedTabsBySession[currentSessionId] || [];
      },

      getGroupedClosedTabs: () => {
        const closedTabs = get().getClosedTabs();
        const groups: Map<string, TabHistoryGroup> = new Map();

        for (const tab of closedTabs) {
          const connectionId = tab.connectionInfo?.connectionId || 'no-connection';
          const existing = groups.get(connectionId);

          if (existing) {
            existing.closedTabs.push(tab);
          } else {
            groups.set(connectionId, {
              connectionId,
              connectionName: tab.connectionInfo?.connectionName || 'No Connection',
              serverHost: tab.connectionInfo?.serverHost,
              closedTabs: [tab],
            });
          }
        }

        return Array.from(groups.values());
      },

      removeClosedTab: (id) => {
        const { currentSessionId } = get();
        if (!currentSessionId) return;

        set((state) => {
          const sessionTabs = state.closedTabsBySession[currentSessionId] || [];
          return {
            closedTabsBySession: {
              ...state.closedTabsBySession,
              [currentSessionId]: sessionTabs.filter((tab) => tab.id !== id),
            },
          };
        });
      },

      clearClosedTabs: () => {
        const { currentSessionId } = get();
        if (!currentSessionId) return;

        set((state) => {
          const newHistory = { ...state.closedTabsBySession };
          delete newHistory[currentSessionId];
          return { closedTabsBySession: newHistory };
        });
      },

      clearSessionHistory: (sessionId) => {
        set((state) => {
          const newHistory = { ...state.closedTabsBySession };
          delete newHistory[sessionId];
          return { closedTabsBySession: newHistory };
        });
      },

      openDialog: () => {
        set({ isDialogOpen: true });
      },

      closeDialog: () => {
        set({ isDialogOpen: false });
      },
    }),
    {
      name: 'sql-studio-tab-history',
      storage: createGuardedJsonStorage({ onFailure: showPersistWriteFailureToast('closed-tab history') }),
      // Only persist closed tabs, NOT the currentSessionId
      // The session store is the source of truth for which session is active.
      // Scripts past the persisted-SQL cap are stripped from the snapshot only
      // (in-memory entries keep them, so reopening within a run is lossless).
      partialize: (state) => ({
        closedTabsBySession: capClosedTabsForPersistence(state.closedTabsBySession),
      }),
      // Convert Date strings back to Date objects on rehydration
      onRehydrateStorage: () => (state) => {
        if (!state?.closedTabsBySession) return;
        let rewrite = false;
        if (typeof state.closedTabsBySession !== 'object' || Array.isArray(state.closedTabsBySession)) {
          // A damaged file: not a session map — dropped, fail closed (review round 4, Fable N5).
          state.closedTabsBySession = {};
          rewrite = true;
        }
        for (const sessionId of Object.keys(state.closedTabsBySession)) {
          const stored = state.closedTabsBySession[sessionId];
          // An entry list or entry that is not a record cannot be read, so it is not kept (review rounds 5–6, N5 /
          // R6-2 — `null` included); one session's damage never aborts the others (fail closed, alone).
          try {
            const entries = recordList<TabHistoryEntry>(stored);
            if (entries !== stored) rewrite = true;
            state.closedTabsBySession[sessionId] = entries.map((entry) => {
              // An older build persisted raw target text in titles / names / hosts (security slice). The date is read
              // on its own and can never throw (review round 3: `{"toString":0}` aborted the sanitization of everything).
              const dated = { ...entry, closedAt: storedDate(entry.closedAt) };
              const safe = safeTab(dated);
              if (safe !== dated) rewrite = true;
              return safe;
            });
          } catch {
            state.closedTabsBySession[sessionId] = [];
            rewrite = true;
          }
        }
        // Those fields are rewritten in the stored file once, right after the load, when that changed anything — and
        // nothing else in it (Fable review S6; review round 2: ordinary persistence would cap SQL bodies). A
        // microtask, as the store may still be being created.
        if (rewrite) queueMicrotask(() => rewritePersistedState('sql-studio-tab-history', safeStoredHistory));
      },
    }
  )
);
